import type { AppSdk } from './sdk';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { eventBusHub } from '@/lib/eventBusHub';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useAuthStore } from '@/store/authStore';
import { settingsStore } from '@/lib/settingsStore';
import { useAppBadgesStore } from '@/lib/appBadges';
import { createLogger } from '@/lib/logger';
import { friendlyError } from '@/lib/errors';

const log = createLogger('AppSDK');

/** Версия контракта AppSDK (не версия платформы). Расширение — новые группы
 *  функций рядом (минорная версия), старые не меняются (без легаси-поддержки). */
export const APPSDK_VERSION = 1;

// ---------- Разрешения приложения (scope-based) ----------
// Манифест объявляет permissions; платформа выдаёт grantedScopes (по манифесту +
// политике). Методы SDK ПРОВЕРЯЮТ скоуп ДО вызова; нет скоупа → {ok:false,
// PERMISSION_DENIED}. Расширяется без легаси: новый скоуп = новое значение.
export type AppScope =
  | 'data:read'       // data.query (HydraQL чтение) + чтение объектов records/objects
  | 'data:write'      // создание/изменение/удаление ОБЪЕКТОВ любых типов (records/objects)
  | 'files'           // файлы: upload/list/download/thumb (FileService)
  | 'equipment'       // подключаемое оборудование (EquipmentService: драйверы/тест/команды)
  | 'ai'              // ИИ: агенты/провайдеры/инструменты/память (AiService — AskAgent)
  | 'adapters'        // адаптеры/боты/сессии/прокси (AdapterService — внешние системы)
  | 'external'        // внешние API: DaData/OSM/STT-whisper (ExternalApiService)
  | 'extensions'      // дополнения платформы (FieldExtensions/реестр возможностей)
  | 'metadata:write'  // создание/изменение/удаление МЕТАДАННЫХ (справочники/документы/
                      // регистры/типы задач-заявок/поля/формы/модули) — как конфигуратор
  | 'messenger'       // чат (ядро): отправить/получить сообщение, контекстное обсуждение
                      // объекта (getOrCreateContextThread) — РАБОТАЕТ без UI чата
  | 'jobs'            // таск-менеджер ядра: фоновые задания (enqueue/status/cancel)
  | 'billing'         // биллинг: счета/операции/доступы/тарифы/подписки (BillingService)
  | 'notify'          // тост/бейдж
  | 'settings:app'    // user_settings app.<code>.* (getSetting/setSetting)
  | 'nav'             // навигация (openObject/section/app/url/back)
  | 'events'          // emit/subscribe/observe (локальная шина UI)
  | 'app_streams'     // streams: реалтайм-топики app.<code>.* (через сервер)
  | 'expose';         // registerAction (предоставить возможности другим)

export const ALL_APP_SCOPES: AppScope[] = [
  'data:read', 'data:write', 'files', 'equipment', 'ai', 'adapters', 'external', 'extensions', 'metadata:write', 'messenger', 'jobs', 'billing',
  'notify', 'settings:app', 'nav', 'events', 'app_streams', 'expose',
];

// ---------- Capabilities (детект ФИЧ, не платформы; P1 Луны) ----------
// Приложение узнаёт ВОЗМОЖНОСТИ среды (браузер/PWA/desktop-Wails/нативный мобайл)
// по фичам, а не по isMobile/isWails. Набор среды вычисляется один раз при старте.
export type AppCapability =
  | 'fullscreen'        // полноэкранный режим (PWA standalone/desktop)
  | 'file_system'       // нативная ФС (Wails); в PWA — нет
  | 'camera'            // getUserMedia
  | 'microphone'        // getUserMedia
  | 'geolocation'       // navigator.geolocation
  | 'push'              // Web Push (secure context)
  | 'notifications'     // Notification API
  | 'video_call'        // LiveKit (media-сервис настроен)
  | 'equipment'         // подключаемое оборудование доступно (сервер)
  | 'wasm_threads'      // SharedArrayBuffer + COOP/COEP
  | 'offline_queue'     // durable offline-очередь (IndexedDB)
  | 'app_streams'       // реалтайм-шина приложения (app.<code>.*, сервер/сервисный токен)
  | 'single_window';    // одно окно (сейчас); Wails3 multi-window — будет

function detectCapabilities(): Set<AppCapability> {
  const caps = new Set<AppCapability>();
  const env = typeof navigator !== 'undefined' ? navigator : undefined;
  if (typeof window !== 'undefined') {
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches;
    const isWails = !!(window as any).go?.main?.App;
    if (standalone || isWails) caps.add('fullscreen');
    if (isWails) caps.add('file_system');
  }
  if (env?.mediaDevices?.getUserMedia) {
    caps.add('camera');
    caps.add('microphone');
  }
  if (env?.geolocation) caps.add('geolocation');
  const secure = typeof window !== 'undefined' && window.isSecureContext;
  if (secure && env?.serviceWorker) caps.add('push');
  if (typeof Notification !== 'undefined') caps.add('notifications');
  if (typeof SharedArrayBuffer !== 'undefined') caps.add('wasm_threads');
  caps.add('video_call'); // LiveKit отдаёт сервер при необходимости (media-settings)
  caps.add('equipment');
  caps.add('offline_queue');
  caps.add('app_streams');
  caps.add('single_window');
  return caps;
}

let cachedCaps: Set<AppCapability> | null = null;
export function appCapabilities(): Set<AppCapability> {
  if (!cachedCaps) cachedCaps = detectCapabilities();
  return cachedCaps;
}


export function isAppScope(v: string): v is AppScope {
  return (ALL_APP_SCOPES as string[]).includes(v);
}

export type ApiResult<T> = { ok: true; value: T } | { ok: false; error: string };

export type AppArea = 'full' | 'window';

export interface AppManifest {
  code: string;
  name: string;
  synonym: string;
  appKind: string;              // рендерер: 'chat' | 'default' | ...
  area: AppArea;                // чат по умолчанию full (весь воркспейс)
  apiVersion: number;
  /** Запрашиваемые разрешения (проверяются ДО каждого вызова SDK) */
  permissions: AppScope[];
}

/** Все скоупы (для системных/доверенных приложений) */
export const ALL_SCOPES_GRANTED = (): AppScope[] => [...ALL_APP_SCOPES];

type Unsub = () => void;

// ---------- Группы функций ядра (см. справку) ----------


export interface AppRenderer {
  /** Вид приложения (ключ рендерера; обычно совпадает с кодом приложения) */
  appKind: string;
  /** Код приложения (kind='app'.code) — по нему открывается приложение из дока;
   *  по умолчанию совпадает с appKind. ЯВНЫЙ, чтобы приложение можно было
   *  открыть БЕЗ обращения к метаданным (манифест — sdk.page.getAppManifest —
   *  обогащает имя/область, но НЕ является условием запуска: рендерер — SSoT
   *  того, что реализовано В ЭТОЙ сборке). */
  code?: string;
  /** Заголовок (для док-панели) */
  title: string;
  /** Компонент приложения (получает sdk в пропсе) */
  Component: React.ComponentType<{ sdk: AppSdk }>;
  /** Манифест приложения (синоним/область/разрешения) — используется, если
   *  серверный манифест недоступен; серверный (при наличии) имеет приоритет. */
  manifest?: AppManifest;
}

export const renderers = new Map<string, AppRenderer>();
/** Реестр по КОДУ приложения — открытие из дока/меню «Пуск» (без метаданных). */
export const renderersByCode = new Map<string, AppRenderer>();

/** registerAppRenderer — реестр рендереров приложений (расширяется: чат/Excel/CS/браузер…). */
export function registerAppRenderer(renderer: AppRenderer): void {
  if (renderers.has(renderer.appKind)) {
    throw new Error(`app renderer '${renderer.appKind}' уже зарегистрирован`);
  }
  const code = renderer.code || renderer.appKind;
  if (renderersByCode.has(code)) {
    throw new Error(`app renderer code '${code}' уже зарегистрирован`);
  }
  const entry: AppRenderer = { ...renderer, code };
  renderers.set(entry.appKind, entry);
  renderersByCode.set(code, entry);
}

export function getAppRenderer(appKind: string): AppRenderer | undefined {
  return renderers.get(appKind);
}

/** getAppRendererByCode — рендерер по КОДУ приложения (годный из дока «Пуск»). */
export function getAppRendererByCode(code: string): AppRenderer | undefined {
  return renderersByCode.get(code);
}

export function listAppRenderers(): AppRenderer[] {
  return Array.from(renderers.values());
}

/** resolveAppManifest — манифест для запуска приложения. appKind/код ВСЕГДА из
 *  рендерера (что реально реализовано в сборке); серверный манифест (если
 *  получен) уточняет отображаемые данные (синоним/область). Серверный RPC
 *  НЕ является условием запуска — иначе приложение с готовым рендерером
 *  падало бы «404/unimplemented» при недоступном манифесте. */
export function resolveAppManifest(renderer: AppRenderer, server: AppManifest | null): AppManifest {
  const code = renderer.code || renderer.appKind;
  if (renderer.manifest) {
    return {
      ...renderer.manifest,
      code,
      appKind: renderer.appKind,
      area: server?.area ?? renderer.manifest.area,
      synonym: server?.synonym || renderer.manifest.synonym,
    };
  }
  if (server) {
    return { ...server, code, appKind: renderer.appKind };
  }
  return {
    code,
    name: code,
    synonym: renderer.title,
    appKind: renderer.appKind,
    area: 'full',
    apiVersion: APPSDK_VERSION,
    permissions: [],
  };
}
